import { Injectable } from '@angular/core';
import {
    loadStripe,
    StripeElements,
    PaymentIntentResult,
    Stripe,
    StripeElementsOptionsMode,
    SetupIntentResult,
    PaymentMethodResult,
} from '@stripe/stripe-js';
import { ConfigService } from 'src/app/services/config/config.service';

@Injectable({
    providedIn: 'root',
})
export class StripeElementsService {
    private stripeMain: Stripe;

    constructor(private configService: ConfigService) {}

    private async initStripe(): Promise<Stripe> {
        if (!this.stripeMain) {
            try {
                const apiKey = this.getStripeApiKey();
                this.stripeMain = await loadStripe(apiKey, {
                    stripeAccount: localStorage.getItem('Stripe_Account_ID'),
                });

                if (!this.stripeMain) {
                    throw new Error(
                        'Failed to initialize Stripe. Please check your API key.'
                    );
                }
            } catch (error) {
                throw error;
            }
        }

        return this.stripeMain;
    }

    private getStripeApiKey(): string {
        return this.configService.config.stripeApiKey;
    }

    public async createStripeElementsForPayment(
        amount: number,
        currency: string
    ): Promise<StripeElements> {
        try {
            if (!currency) {
                throw new Error('Currency is required for Stripe Elements');
            }
            const validAmount = amount && amount > 0 ? amount : 0;

            if (!this.stripeMain) {
                this.stripeMain = await this.initStripe();
            }

            const options: StripeElementsOptionsMode = {
                mode: 'payment',
                amount: Math.round(validAmount * 100),
                currency: currency.toLowerCase(),
                paymentMethodCreation: 'manual',
            };

            const stripeElements = this.stripeMain.elements(options);

            if (!stripeElements) {
                throw new Error('Failed to create Stripe Elements');
            }

            return stripeElements;
        } catch (error) {
            throw error;
        }
    }

    public async createStripeElementsForSetup(
        currency: string
    ): Promise<StripeElements> {
        try {
            if (!currency) {
                throw new Error('Currency is required for Stripe Elements');
            }

            if (!this.stripeMain) {
                this.stripeMain = await this.initStripe();
            }

            const options: StripeElementsOptionsMode = {
                mode: 'setup',
                currency: currency.toLowerCase(),
                paymentMethodTypes: ['card'],
                paymentMethodCreation: 'manual',
            };

            const stripeElements = this.stripeMain.elements(options);

            if (!stripeElements) {
                throw new Error('Failed to create Stripe Elements for setup');
            }

            return stripeElements;
        } catch (error) {
            throw error;
        }
    }

    public async createPaymentMethod(
        stripePaymentElements: StripeElements
    ): Promise<PaymentMethodResult> {
        return await this.stripeMain.createPaymentMethod({
            elements: stripePaymentElements,
        });
    }

    public async confirmPayment(
        clientSecret: string,
        stripePaymentElements: StripeElements,
        returnUrl: string
    ): Promise<PaymentIntentResult> {
        return await this.stripeMain.confirmPayment({
            elements: stripePaymentElements,
            clientSecret,
            confirmParams: {
                return_url: returnUrl,
            },
            redirect: 'if_required',
        });
    }

    public async retrievePaymentIntent(
        clientSecret: string
    ): Promise<PaymentIntentResult> {
        return await (await this.initStripe()).retrievePaymentIntent(
            clientSecret
        );
    }

    public async confirmSetupIntent(
        clientSecret: string,
        stripePaymentElements: StripeElements,
        returnUrl: string
    ): Promise<SetupIntentResult> {
        return await this.stripeMain.confirmSetup({
            elements: stripePaymentElements,
            clientSecret,
            confirmParams: {
                return_url: returnUrl,
            },
            redirect: 'if_required',
        });
    }
}
